/**
 * Bays Admin Page
 * CRUD operations for workshop bays (work areas)
 * Feature 009: Workshop Booking & Job-Tracking System
 * T075
 */

import { useState, type ChangeEvent } from 'react';
import { AlertCircle, Plus, Pencil, Wrench, Car, Cpu } from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { Modal } from '../../components/ui/modal';
import { useToast } from '../../contexts/ToastContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { pageStyles, tableStyles } from '../../lib/styles';

// Bay types
const BAY_TYPES = ['mechanical', 'bodywork', 'diagnostics'] as const;
type BayType = typeof BAY_TYPES[number];

interface Bay {
  id: string;
  name: string;
  bayType: BayType;
  maxConcurrentJobs: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateBayInput {
  name: string;
  bayType: BayType;
  maxConcurrentJobs: number;
  isActive: boolean;
}

const BAY_TYPE_LABELS: Record<BayType, string> = {
  mechanical: 'Mecánica',
  bodywork: 'Carrocería',
  diagnostics: 'Diagnóstico',
};

const BAY_TYPE_ICONS: Record<BayType, typeof Wrench> = {
  mechanical: Wrench,
  bodywork: Car,
  diagnostics: Cpu,
};

const emptyDraft: CreateBayInput = {
  name: '',
  bayType: 'mechanical',
  maxConcurrentJobs: 1,
  isActive: true,
};

export function BaysPage() {
  const { success: showSuccess, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(true);

  const { data: bays = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'bays', includeInactive],
    queryFn: () => {
      const params = includeInactive ? '?include_inactive=true' : '';
      return api.get<Bay[]>(`/api/admin/bays${params}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (input: CreateBayInput) =>
      api.post<Bay>('/api/admin/bays', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'bays'] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: input }: { id: string; data: Partial<CreateBayInput> }) =>
      api.patch<Bay>(`/api/admin/bays/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'bays'] }),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Bay | null>(null);
  const [draft, setDraft] = useState<CreateBayInput>(emptyDraft);

  const openCreate = () => {
    setEditing(null);
    setDraft({ ...emptyDraft });
    setIsModalOpen(true);
  };

  const openEdit = (bay: Bay) => {
    setEditing(bay);
    setDraft({
      name: bay.name,
      bayType: bay.bayType,
      maxConcurrentJobs: bay.maxConcurrentJobs,
      isActive: bay.isActive,
    });
    setIsModalOpen(true);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    if (!draft.name.trim()) {
      showError('El nombre es obligatorio');
      return;
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: draft });
        showSuccess('Bahía actualizada');
      } else {
        await createMutation.mutateAsync(draft);
        showSuccess('Bahía creada');
      }
      setIsModalOpen(false);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const toggleActive = async (bay: Bay) => {
    try {
      await updateMutation.mutateAsync({ id: bay.id, data: { isActive: !bay.isActive } });
      showSuccess(!bay.isActive ? 'Bahía activada' : 'Bahía desactivada');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Error al actualizar');
    }
  };

  return (
    <div className={pageStyles.container}>
      <div className={pageStyles.header}>
        <div>
          <h1 className={pageStyles.title}>Bahías de trabajo</h1>
          <p className={pageStyles.subtitle}>Gestiona las áreas físicas del taller</p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-3 w-3 mr-1" />
          Nueva
        </Button>
      </div>

      <div className={tableStyles.container}>
        <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-gray-100">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Mostrar inactivas
          </label>
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Recargar
          </Button>
        </div>

        {isError && (
          <div className="px-3 py-2 bg-red-50 flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-red-500" />
            <span className="text-xs text-red-700">{(error as Error)?.message}</span>
          </div>
        )}

        {!isLoading && !isError && bays.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-gray-500">No hay bahías registradas.</div>
        )}

        {!isLoading && !isError && bays.length > 0 && (
          <table className="w-full">
            <thead>
              <tr className={tableStyles.headerRow}>
                <th className={tableStyles.headerCell}>Nombre</th>
                <th className={tableStyles.headerCell}>Tipo</th>
                <th className={tableStyles.headerCell}>Capacidad</th>
                <th className={tableStyles.headerCell}>Estado</th>
                <th className={`${tableStyles.headerCell} text-right`}>Acciones</th>
              </tr>
            </thead>
            <tbody className={tableStyles.body}>
              {bays.map((bay) => {
                const Icon = BAY_TYPE_ICONS[bay.bayType];
                return (
                  <tr key={bay.id} className={tableStyles.row}>
                    <td className={tableStyles.cell}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-gray-400" />
                        <span className="font-medium">{bay.name}</span>
                      </div>
                    </td>
                    <td className={tableStyles.cell}>{BAY_TYPE_LABELS[bay.bayType]}</td>
                    <td className={tableStyles.cell}>{bay.maxConcurrentJobs} trabajo(s)</td>
                    <td className={tableStyles.cell}>
                      {bay.isActive ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
                          Activa
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                          Inactiva
                        </span>
                      )}
                    </td>
                    <td className={`${tableStyles.cell} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => toggleActive(bay)}
                          disabled={updateMutation.isPending}
                          className="px-2 py-1 text-[10px] font-medium rounded hover:bg-gray-100 text-gray-600 disabled:opacity-50"
                        >
                          {bay.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          onClick={() => openEdit(bay)}
                          className="p-1 rounded hover:bg-gray-100 text-gray-500"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? 'Editar bahía' : 'Nueva bahía'}
        size="sm"
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Nombre *</label>
            <Input
              className="h-8 text-sm"
              value={draft.name}
              onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Bahía 1, Box A, etc."
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Tipo de bahía</label>
            <select
              value={draft.bayType}
              onChange={(e) => setDraft((prev) => ({ ...prev, bayType: e.target.value as BayType }))}
              className="w-full h-8 rounded-md border border-gray-300 px-2 text-sm"
            >
              {BAY_TYPES.map((type) => (
                <option key={type} value={type}>
                  {BAY_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">Trabajos simultáneos</label>
            <Input
              className="h-8 text-sm"
              type="number"
              value={draft.maxConcurrentJobs}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  maxConcurrentJobs: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)),
                }))
              }
              min={1}
              max={10}
            />
            <p className="text-[10px] text-gray-500">Vehículos que pueden atenderse al mismo tiempo.</p>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-3.5 w-3.5"
            />
            Activa (disponible para asignación)
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              Guardar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
